import { EBrowserStorageType } from '@/core/apis/browser_storage_api/browser_storage_api_types';
import { TBrowserApiData } from '@/core/constants/event_types/additional_types';
import { TBrowserData } from '@/core/constants/event_types/base_mable_event';
import { TimeUtils } from '@/core/event_logic/utils/time_utils';
import { UniqueIdUtils } from '@/core/event_logic/utils/unique_id_utils';
import { EDestinationType } from '@/core/interfaces';
import { DestinationCookieInitializer } from '@/destinations/cookie_initializer';
import { Logger } from '@/shared/logger/logger';

import { StorageHandler } from '../storage_handler';

import { DictUtils } from '../../event_logic/utils/dict_utils';

export class BrowserDataHandler {
  private static _storageHandler: StorageHandler<TBrowserData>;

  private static initStorageHandler(timestamp: number): void {
    if (!this._storageHandler) {
      const { newCookies } =
        DestinationCookieInitializer.initializeDestinationCookies(
          {},
          {},
          [],
          timestamp,
        );
      this._storageHandler = new StorageHandler<TBrowserData>(
        'browserData',
        {
          aid: '',
          bsts: timestamp,
          cks: newCookies,
          iqpar: {},
          ir: '',
          is_backend: false,
          srh: 0,
          srw: 0,
          ua: '',
          ul: '',
        },
        EBrowserStorageType.LOCALSTORAGE,
      );
    }
  }

  static getStorageHandler(timestamp: number): StorageHandler<TBrowserData> {
    this.initStorageHandler(timestamp);
    return this._storageHandler;
  }

  private static validateAndFixIqpar(browserData: TBrowserData): TBrowserData {
    return DictUtils.validateAndFixStringArrayDict(browserData, 'iqpar');
  }

  static async initOrLoadAndMaybeUpdate(
    browserApiData: TBrowserApiData,
  ): Promise<{
    browserData: TBrowserData;
    browserDataChanged: boolean;
  }> {
    const timestamp = await TimeUtils.getCurrentEpochInSecondsSynced();
    const handler = this.getStorageHandler(timestamp);
    const existingData = this.validateAndFixIqpar(handler.get());
    const browserDataChanged = this.hasBrowserDataChanged(
      existingData,
      browserApiData,
    );
    const browserExists = !!existingData.aid;

    const initialCookies =
      browserApiData?.cks && Object.keys(browserApiData.cks).length > 0
        ? browserApiData.cks
        : existingData.cks;

    const destinationTypes = [
      EDestinationType.GA4,
      EDestinationType.Facebook,
      EDestinationType.TikTok,
    ];
    const { cookiesChanged, newCookies } =
      DestinationCookieInitializer.initializeDestinationCookies(
        initialCookies,
        browserExists ? existingData.iqpar : browserApiData.qpar,
        destinationTypes,
        timestamp,
      );

    const newBrowserData: TBrowserData = {
      aid: existingData.aid || UniqueIdUtils.getUuidV7(),
      bsts: existingData.bsts,
      cks: newCookies,
      iqpar: browserExists ? existingData.iqpar : browserApiData.qpar,
      ir: browserExists ? existingData.ir : browserApiData.rl,
      is_backend: false,
      srh: browserApiData.srh || existingData.srh,
      srw: browserApiData.srw || existingData.srw,
      ua: browserApiData.ua || existingData.ua,
      ul: browserApiData.ul || existingData.ul,
    };

    handler.set(newBrowserData);
    Logger.debug('Browser data initialized with cookies', {
      browserDataChanged,
      browserExists,
      cookiesChanged,
      newCookies,
    });

    return {
      browserData: newBrowserData,
      browserDataChanged: browserDataChanged || cookiesChanged,
    };
  }

  static get(): TBrowserData {
    return this.validateAndFixIqpar(this._storageHandler.get());
  }

  private static hasBrowserDataChanged(
    existingData: TBrowserData,
    browserApiData: TBrowserApiData,
  ): boolean {
    return (
      browserApiData.srh !== existingData.srh ||
      browserApiData.srw !== existingData.srw ||
      browserApiData.ua !== existingData.ua ||
      browserApiData.ul !== existingData.ul
    );
  }
}
